/**
 * Admin Entries extension hooks.
 *
 * Powers two related admin surfaces:
 *   1. User → Events tab on the admin User Details page
 *      (`useAdminUserEntries`)
 *   2. The cross-org entries ledger page used by admins and delegates
 *      (`useAdminEntriesLedger`)
 *
 * Both hit /api/admin-ops endpoints. The backend handles org-scope
 * filtering — these hooks just shape the request and cache the response.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Types ---
// 'processing' = an online payment (typically ACH/bank) submitted to Stripe
// but not yet settled; funds are in flight, so it's neither paid nor unpaid.
export type PaymentStatus =
  | 'paid'
  | 'unpaid'
  | 'partial'
  | 'processing'
  // Micro-deposit ACH awaiting the contestant's bank verification (the payment
  // is 'pending' + requires_action — acted on, but no debit submitted yet).
  | 'verifying'
  | 'free';

// Detail-panel field types. `payment_method` is null on legacy rows that
// predate the column — the UI renders "Unknown" in that case. `created_by`
// is null for pre-column rows too; `paid_by` and `responsible_party` are
// mutually exclusive (paid_by present iff the row has cleared payment).
export type LedgerPaymentMethod =
  | 'stripe_card'
  | 'bank'
  | 'pay_later'
  | 'cash'
  | 'check';

export type LedgerCreatedByRole =
  | 'self'
  | 'buddy_group_primary'
  | 'team_roping_member';

// Canonical team-roping partner status, straight off the registration row's
// `partner_status` column. Mirrors the DB CHECK constraint:
//   pending        invitation outstanding (waiting on the partner)
//   approved       partner accepted
//   rejected       partner declined the invite
//   cancelled      team withdrawn / invite withdrawn
//   needs_partner  prior partner fell through; primary needs to re-pair
//   none           individual entry, no partner involvement (solo)
export type LedgerPartnerStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'needs_partner'
  | 'none';

export interface LedgerCreatedBy {
  user_id: string;
  name: string;
  role: LedgerCreatedByRole;
}

export interface LedgerActorRef {
  user_id: string;
  name: string;
}

// Fee breakdown surfaced in the expanded row panel. `transaction_fee` is the
// dynamically-calculated Stripe surcharge for auto-mode events — the backend
// falls back to (payment_amount − entry_fee − non_member_fee) when the
// event row's stored value is 0, so this is always the *effective* charge.
export interface LedgerFeeBreakdown {
  entry_fee: number;
  transaction_fee: number;
  non_member_fee: number;
  total: number;
}

export interface AdminEntryRow {
  registration_id: string;
  user_profile_id?: string;
  // Pre-composed string (admin-list default — legal-name-first). Kept for
  // callers that haven't migrated to the split fields below.
  contestant_name?: string | null;
  // Split-out names. Legal pair is sourced from the canonical user_profiles
  // values (with a fallback to the registration's denormalized trig_*
  // snapshot for very old rows). Display pair is null unless the user has
  // explicitly set a display name — never derived from legal.
  legal_first_name?: string | null;
  legal_last_name?: string | null;
  display_first_name?: string | null;
  display_last_name?: string | null;
  contestant_email?: string | null;
  event_id: string | null;
  event_name: string | null;
  event_type: string | null;
  rodeo_id: string | null;
  rodeo_name: string | null;
  /**
   * The parent RODEO was cancelled — distinct from `is_cancelled`, which is
   * about this entry. A cancelled rodeo's entries owe nothing (the ledger's
   * due/outstanding columns read $0) and stay cancellable even after the draw.
   *
   * Optional: rows cached by React Query from before this field shipped won't
   * carry it, and absent must read as "not cancelled" rather than crash.
   */
  rodeo_cancelled?: boolean;
  rodeo_start_date: string | null;
  rodeo_end_date: string | null;
  organization_id: string | null;
  organization_name: string | null;
  registration_status: string | null;
  registration_type: string | null;
  position: string | null;
  partner_name: string | null;
  // Team-roping partner card fields. `is_team_roping` gates whether the
  // ledger renders a partner card for this entry; the partner fields below
  // reflect the *latest* partner selected (the registration row's columns
  // are overwritten in place when a new partner is invited, so a previously
  // rejected partner never leaks through). All optional so older API
  // responses (or non-team-roping rows) don't break the UI.
  is_team_roping?: boolean;
  partner_user_profile_id?: string | null;
  partner_status?: LedgerPartnerStatus | null;
  amount_due: number;
  // Net (current) paid balance after refunds — what the per-row Paid cell
  // displays. Cancelled+refunded rows land at 0 here even when a real
  // payment was made; see `amount_paid_gross` + `amount_refunded` for the
  // lifetime breakdown.
  amount_paid: number;
  // Lifetime gross paid for this entry — sum of completed payments +
  // credit adjustments, untouched by refunds. Used by the ledger to render
  // "was paid, then refunded $X" on cancelled rows and to roll up an
  // honest `totalPaid` summary card.
  amount_paid_gross: number;
  // Lifetime refunded — sum of completed payment_refunds + debit
  // adjustments. Feeds the per-row refund display and the Refunded
  // summary card.
  amount_refunded: number;
  // True if a payment ever cleared on this entry (paidGross > $0). Lets
  // cancelled rows render "paid" or "unpaid" based on historical state
  // rather than the always-$0 net balance after a full refund.
  was_paid: boolean;
  payment_status: PaymentStatus;
  is_cancelled: boolean;
  created_at: string;
  // Derived "last activity" timestamp: max of the entry's own updated_at
  // and the most recent payment/refund/adjustment touching the entry. This
  // is what the Updated column on the ledger sorts by — so a pay-later
  // registration transitioning to paid bumps Updated even though the entry
  // row itself didn't change.
  last_updated_at: string;
  // Row-detail panel fields. Populated by the backend; all optional so
  // older API responses (or rows that pre-date the source columns) don't
  // break the UI — the panel renders "Unknown" / "—" in those cases.
  payment_method?: LedgerPaymentMethod | null;
  created_by?: LedgerCreatedBy | null;
  // paid_by and responsible_party are mutually exclusive: paid_by is
  // present iff the row has cleared payment, otherwise responsible_party
  // identifies who's on the hook (defaulting to the contestant, overridden
  // when a team-roping partner has taken payment responsibility).
  paid_by?: LedgerActorRef | null;
  responsible_party?: LedgerActorRef | null;
  // Fee breakdown. Optional so older API responses don't break the UI —
  // the panel falls back to "—" when missing.
  fee_breakdown?: LedgerFeeBreakdown | null;
  // Split-tender: portion of the fee settled with org account credit at
  // checkout (already inside amount_paid_gross). >0 means the detail panel
  // should break the settlement into "card $X + credit $Y" rather than a
  // single opaque paid figure. Optional/defaulted for older API responses.
  credit_applied?: number;
}

export interface UserEntriesResponse {
  current: AdminEntryRow[];
  past: AdminEntryRow[];
  totals: {
    currentCount: number;
    pastCount: number;
    totalDue: number;
    // Net intake: completed payments minus refunds (cancelled entries
    // included, so money retained on a cancelled entry still counts).
    // Refunds are reported separately as totalRefunded — add the two for
    // lifetime gross. Matches the entries-ledger definition.
    totalPaid: number;
    totalRefunded: number;
    // Unpaid balance on non-cancelled entries (in-flight ACH excluded — it's
    // submitted and just clearing). Optional so an older API response renders
    // $0 rather than NaN.
    totalOutstanding?: number;
    // Net money kept on cancelled entries (a retained transaction fee, or a
    // cancellation never refunded). Inside totalPaid but with no counterpart
    // in totalDue, so the tab surfaces it to explain a paid figure that runs
    // ahead of fees due.
    totalRetainedOnCancelled?: number;
  };
}

// Sort columns the ledger table exposes. Kept in sync with the backend
// EntriesLedgerQuerySchema sortBy union so the server can paginate stably.
export type LedgerSortKey =
  | 'contestant'
  | 'event'
  | 'rodeo'
  | 'created_at'
  | 'last_updated_at'
  | 'fee'
  | 'paid'
  | 'status';

export interface EntriesLedgerFilters {
  orgId?: string;
  rodeoId?: string;
  eventId?: string;
  paymentStatus?: 'paid' | 'unpaid' | 'partial' | 'processing' | 'verifying';
  // Server-side search + pagination + sort. The ledger is paginated like the
  // Manage Users page; search/sort run server-side so paging stays stable.
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: LedgerSortKey;
  sortOrder?: 'asc' | 'desc';
}

export interface EntriesLedgerResponse {
  entries: AdminEntryRow[];
  // Pagination envelope — matches the /admin-ops/users list contract.
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  totals: {
    contestantCount: number;
    registrationCount: number;
    paidCount: number;
    unpaidCount: number;
    partialCount: number;
    processingCount: number;
    // Micro-deposit ACH entries awaiting the contestant's bank verification.
    verifyingCount: number;
    freeCount: number;
    totalDue: number;
    // Net intake across the filtered slice: completed payments minus refunds
    // (cancelled entries included, so a retained transaction fee still
    // counts). Refunds are reported separately as totalRefunded — add the two
    // for lifetime gross — which keeps
    // `totalPaid + totalOutstanding ≈ totalDue` on the summary card.
    totalPaid: number;
    totalRefunded: number;
    // Sum of unpaid balance on non-cancelled entries only — what
    // contestants still actually owe. Server-side per-row sum so
    // residual net intake from cancelled-and-refunded entries does NOT
    // bleed into the outstanding figure.
    totalOutstanding: number;
  };
  scope: { orgIds: string[] | null; appliedOrgId?: string };
}

// --- Query Key Factory ---
//
// Conditionally omit the filters payload from the key when no filters are
// supplied — without that, calling `useAdminEntriesLedger()` produces a key
// with `undefined` in it, which breaks `invalidateQueries` prefix matching
// (partialDeepEqual fails on `undefined` vs a real filter object).
export const adminEntriesKeys = {
  all: ['admin-ops', 'entries'] as const,
  userEntries: (userId: string) =>
    [...adminEntriesKeys.all, 'user', userId] as const,
  ledger: (filters?: EntriesLedgerFilters) =>
    filters
      ? ([...adminEntriesKeys.all, 'ledger', filters] as const)
      : ([...adminEntriesKeys.all, 'ledger'] as const),
};

// --- Query Hooks ---
export function useAdminUserEntries(userId: string | null | undefined) {
  return useQuery({
    queryKey: adminEntriesKeys.userEntries(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      httpClient.get<UserEntriesResponse>(
        `/admin-ops/users/${userId}/entries`,
      ),
  });
}

export function useAdminEntriesLedger(filters?: EntriesLedgerFilters) {
  const qs = new URLSearchParams();
  if (filters?.orgId) qs.set('orgId', filters.orgId);
  if (filters?.rodeoId) qs.set('rodeoId', filters.rodeoId);
  if (filters?.eventId) qs.set('eventId', filters.eventId);
  if (filters?.paymentStatus) qs.set('paymentStatus', filters.paymentStatus);
  if (filters?.search) qs.set('search', filters.search);
  if (filters?.page) qs.set('page', String(filters.page));
  if (filters?.pageSize) qs.set('pageSize', String(filters.pageSize));
  if (filters?.sortBy) qs.set('sortBy', filters.sortBy);
  if (filters?.sortOrder) qs.set('sortOrder', filters.sortOrder);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: adminEntriesKeys.ledger(filters),
    queryFn: ({ signal }) =>
      httpClient.get<EntriesLedgerResponse>(`/admin-ops/entries-ledger${query}`, {
        signal,
      }),
    // Keep the previous page visible while the next one loads so paging /
    // sorting doesn't flash an empty table.
    placeholderData: keepPreviousData,
  });
}

// --- Mutation Hooks ---

export interface MarkEntryPaidResult {
  success: boolean;
  registration_id: string;
  payment_id: string;
  method: 'cash' | 'check';
  amount: number;
  status: string;
  cancelled_stripe_payment_ids: string[];
}

/**
 * Admin/delegate: record that an entry fee was paid offline (cash or check).
 * Confirms the registration and clears it from the contestant's outstanding
 * balance server-side. Invalidates every surface that reflects payment state
 * (the admin entries ledger + per-user entries, plus the contestant-facing
 * Funds tab / outstanding-balance + dashboard) so the change shows everywhere
 * without a manual refresh.
 */
export function useMarkEntryPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      registrationId: string;
      method: 'cash' | 'check';
      note?: string;
      // Fee controls: waive the processing surcharge outright, override it with
      // a custom amount, and/or override the entry fee. Waive takes precedence
      // over feeOverride server-side.
      waiveTransactionFee?: boolean;
      feeOverride?: number;
      entryFeeOverride?: number;
    }) =>
      httpClient.post<MarkEntryPaidResult>(
        `/registration-payment-ops/mark-paid/${vars.registrationId}`,
        {
          method: vars.method,
          ...(vars.note ? { note: vars.note } : {}),
          ...(vars.waiveTransactionFee ? { waiveTransactionFee: true } : {}),
          ...(vars.feeOverride != null ? { feeOverride: vars.feeOverride } : {}),
          ...(vars.entryFeeOverride != null
            ? { entryFeeOverride: vars.entryFeeOverride }
            : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminEntriesKeys.all });
      // Contestant-facing surfaces (Funds tab, header balance badge, etc.).
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

export interface AdjustEntryFeeResult {
  success: boolean;
  registration_id: string;
  entry_fee: number;
  transaction_fee: number;
  non_member_fee: number;
  payment_amount: number;
}

/**
 * Admin/delegate: adjust the fees on an UNPAID entry. Each fee is independent
 * and OPTIONAL — omit one to leave it untouched. Send all three as 0 to fully
 * waive a drawn-out contestant, who keeps their drawn slot but owes nothing
 * (the $10 office fee is then added as a manual ledger charge, and they get a
 * DNP in live scoring).
 *
 * Omission matters: sending `newTransactionFee` pins a per-entry override, so
 * a fee the admin didn't touch must NOT be echoed back, or an auto-resolved
 * surcharge would be frozen at today's value.
 *
 * The backend refuses paid entries and guards the pay-just-before-edit race.
 * Invalidates the same surfaces as mark-paid so the new owed amount shows
 * everywhere.
 */
export function useAdjustEntryFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      registrationId: string;
      newEntryFee?: number;
      newTransactionFee?: number;
      newNonMemberFee?: number;
    }) =>
      httpClient.post<AdjustEntryFeeResult>(
        `/registration-payment-ops/adjust-entry-fee/${vars.registrationId}`,
        {
          ...(vars.newEntryFee != null ? { newEntryFee: vars.newEntryFee } : {}),
          ...(vars.newTransactionFee != null
            ? { newTransactionFee: vars.newTransactionFee }
            : {}),
          ...(vars.newNonMemberFee != null
            ? { newNonMemberFee: vars.newNonMemberFee }
            : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminEntriesKeys.all });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}

/**
 * Admin/delegate: cancel an entry that is NOT yet in a draw. The backend
 * refuses entries already placed in a draw (409 ENTRY_IN_DRAW) — those must be
 * waived to $0 + given a DNP in live scoring instead, UNLESS the parent rodeo
 * has been cancelled, in which case the draw guard is lifted (there is no
 * running order left to protect). Refunds any captured payment by default; the
 * transaction fee stays non-refundable unless the caller says otherwise or the
 * rodeo was cancelled, in which case the server refunds it too.
 *
 * Leave `refundTransactionFee` undefined to inherit that server-side default —
 * sending an explicit `false` overrides it and makes the contestant eat the
 * processor's cut for a cancellation that was not their doing.
 */
export function useCancelEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      registrationId: string;
      issueRefund?: boolean;
      refundTransactionFee?: boolean;
    }) =>
      httpClient.post<{ success: boolean }>(
        `/registration-payment-ops/cancel-entry/${vars.registrationId}`,
        {
          ...(vars.issueRefund != null ? { issueRefund: vars.issueRefund } : {}),
          ...(vars.refundTransactionFee != null
            ? { refundTransactionFee: vars.refundTransactionFee }
            : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminEntriesKeys.all });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
      qc.invalidateQueries({ queryKey: ['payment-ops'] });
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
    },
  });
}
